import React from 'react';
import Select from 'react-select';
import {
  Button,
  Card,
  CardBody,
  Col,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Row,
} from 'reactstrap';
import { FiArrowLeft } from 'react-icons/fi';

const CreateLead = ({
  createLeadSections,
  selectFieldStyles,
  leadImageInputRef,
  leadImageName,
  leadImagePreviewUrl,
  formValues,
  formErrors,
  submittingLead,
  leadOwnerOptions,
  polymerTypeOptions,
  countries,
  currencies,
  statesData,
  citiesData,
  staffListLoading,
  staffListError,
  productListLoading,
  fetchStatesByCountry,
  fetchCitiesByState,
  onClose,
  onSubmit,
  onInputChange,
  onCreateLeadUploadChange,
  onLeadImageChooseFile,
  onLeadImageFileChange,
}) => {
  const renderCreateLeadField = (field) => (
    <FormGroup className="crm-lead-create__field" key={field.key}>
      <Label className="crm-lead-create__label">
        {field.label}
        {field.required ? ' *' : ''}
      </Label>
      {(() => {
        let selectOptions = field.options || [];
        if (field.key === 'leadOwner') {
          selectOptions = leadOwnerOptions;
        } else if (field.key === 'typeOfPolymer') {
          selectOptions = polymerTypeOptions;
        }
        if (field.key === 'countryId') {
          selectOptions = countries;
        } else if (field.key === 'stateId') {
          selectOptions = (statesData?.statesByCountry || []).map(s => ({ label: s.stateName, value: s.id }));
        } else if (field.key === 'cityId') {
          selectOptions = (citiesData?.citiesByState || []).map(c => ({ label: c.cityName, value: c.id }));
        } else if (field.key === 'currency') {
          selectOptions = currencies;
        }

        const hasNoneOption = selectOptions.some((option) => option === '-None-');
        const normalizedOptions = hasNoneOption
          ? selectOptions.filter((option) => option !== '-None-')
          : selectOptions;
        const menuPortalTarget =
          typeof document === 'undefined' ? null : document.body;

        if (field.type === 'select') {
          const selectReactOptions = normalizedOptions.map((option) => (
            typeof option === 'object' && option !== null
              ? { label: option.label, value: option.value }
              : { label: option, value: option }
          ));

          const currentValue = formValues[field.key];
          const selectedValueObj = currentValue
            ? selectReactOptions.find(o => String(o.value) === String(currentValue)) || { label: currentValue, value: currentValue }
            : null;

          return (
            <Select
              className="crm-lead-create__select"
              classNamePrefix="crm-create-select"
              options={selectReactOptions}
              value={selectedValueObj}
              onChange={(selectedOption) => {
                const val = selectedOption?.value || '';
                onInputChange(field.key, val);

                if (field.key === 'countryId') {
                  onInputChange('stateId', '');
                  onInputChange('cityId', '');
                  if (val) fetchStatesByCountry({ variables: { country: Number(val) } });
                } else if (field.key === 'stateId') {
                  onInputChange('cityId', '');
                  if (val) fetchCitiesByState({ variables: { state: Number(val) } });
                }
              }}
              placeholder={hasNoneOption ? '-None-' : `Select ${field.label}`}
              isSearchable
              isLoading={
                (field.key === 'leadOwner' && staffListLoading) ||
                (field.key === 'typeOfPolymer' && productListLoading)
              }
              isDisabled={
                (field.key === 'leadOwner' && (staffListLoading || Boolean(staffListError))) ||
                (field.key === 'typeOfPolymer' && productListLoading)
              }
              noOptionsMessage={() => {
                if (field.key === 'leadOwner' && staffListError) {
                  return 'Unable to load users';
                }

                return field.key === 'leadOwner' ? 'No users found' : 'No options found';
              }}
              styles={selectFieldStyles}
              menuPlacement="auto"
              menuPortalTarget={menuPortalTarget}
            />
          );
        }

        if (field.type === 'upload') {
          return (
            <div className="crm-lead-create__attachment-field">
              <div className="crm-lead-create__attachment-row">
                <label className="crm-lead-create__attachment-btn">
                  <input
                    type="file"
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.mp3,.wav,.ogg"
                    onChange={(e) => onCreateLeadUploadChange(field.key, e)}
                  />
                  Choose File
                </label>
                <span className="crm-lead-create__attachment-name" title={formValues[field.key]}>
                  {formValues[field.key] && formValues[field.key].startsWith('http') ? (
                    <a href={formValues[field.key]} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all' }}>
                      {formValues[field.key]}
                    </a>
                  ) : (
                    formValues[field.key] || 'No file chosen'
                  )}
                </span>
              </div>
            </div>
          );
        }

        if (field.type === 'textarea') {
          return (
            <Input
              type="textarea"
              className="crm-lead-create__control crm-lead-create__control--textarea"
              value={formValues[field.key]}
              invalid={Boolean(formErrors[field.key])}
              onChange={(e) => onInputChange(field.key, e.target.value)}
            />
          );
        }

        return (
          <Input
            type={field.type === 'number' ? 'number' : field.type}
            className="crm-lead-create__control"
            value={formValues[field.key]}
            invalid={Boolean(formErrors[field.key])}
            onChange={(e) => onInputChange(field.key, e.target.value)}
          />
        );
      })()}
      <FormFeedback>{formErrors[field.key]}</FormFeedback>
    </FormGroup>
  );

  const renderCreateLeadSection = (title, leftFields, rightFields) => (
    <section className="crm-lead-create__section" key={title}>
      <div className="crm-lead-create__section-head">
        <h5>{title}</h5>
      </div>
      <Row className="g-3 crm-lead-create__grid">
        <Col md={6}>
          <div className="crm-lead-create__column">
            {leftFields.map((field) => renderCreateLeadField(field))}
          </div>
        </Col>
        <Col md={6}>
          <div className="crm-lead-create__column">
            {rightFields.map((field) => renderCreateLeadField(field))}
          </div>
        </Col>
      </Row>
    </section>
  );

  return (
    <div className="crm-lead-page__create-view">
      <div className="crm-lead-page__create-topbar">
        <div className="crm-lead-page__detail-heading">
          <Button
            color="link"
            className="crm-lead-page__back-btn"
            onClick={onClose}
          >
            <FiArrowLeft size={18} />
          </Button>
          <div className="crm-lead-page__detail-title-wrap">
            <h4 className="crm-lead-page__detail-title">Create Lead</h4>
          </div>
        </div>
      </div>

      <Card className="crm-lead-create__card">
        <CardBody className="crm-lead-create__body">
          <form onSubmit={onSubmit}>
            <div className="crm-lead-create__image-block">
              <div className="crm-lead-create__image-label">Lead Image</div>
              <input
                ref={leadImageInputRef}
                type="file"
                className="d-none"
                accept="image/*"
                onChange={onLeadImageFileChange}
              />
              <div className="crm-lead-create__image-row">
                <div className="crm-lead-create__image-avatar" aria-hidden="true">
                  {leadImagePreviewUrl ? (
                    <img
                      src={leadImagePreviewUrl}
                      alt="Lead"
                      style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
                    />
                  ) : leadImageName ? (
                    leadImageName.slice(0, 2).toUpperCase()
                  ) : ''}
                </div>
                <div className="crm-lead-create__image-meta">
                  <Button
                    type="button"
                    color="light"
                    className="crm-lead-create__upload-btn border"
                    onClick={onLeadImageChooseFile}
                  >
                    Upload Image
                  </Button>
                  <span className="crm-lead-create__file-name" title={leadImageName}>
                    {leadImageName && leadImageName.startsWith('http') ? (
                      <a href={leadImageName} target="_blank" rel="noopener noreferrer" style={{ wordBreak: 'break-all' }}>
                        {leadImageName}
                      </a>
                    ) : (
                      leadImageName || 'No file chosen'
                    )}
                  </span>
                </div>
              </div>
            </div>

            {createLeadSections.map((section) =>
              renderCreateLeadSection(
                section.title,
                section.leftFields,
                section.rightFields,
              ),
            )}

            <div className="crm-lead-create__footer">
              <Button
                type="button"
                color="secondary"
                outline
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button type="submit" color="primary" disabled={submittingLead}>
                {submittingLead ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  );
};

export default CreateLead;
